import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { AdminUser, CreateUserPayload, UpdateUserPayload } from '../../types/auth'
import { FormField, Input, Select, Row } from '../../components/ui/FormField'
import { Button } from '../../components/ui/Button'
import { tokens } from '../../styles/tokens'

type Provider = 'google' | 'apple'

interface Props {
  initial?: AdminUser | null
  onChange: (payload: CreateUserPayload | UpdateUserPayload) => void
  onClearIntegration?: (provider: Provider) => void
}

const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: ${tokens.purple900};
`

const ToggleTrack = styled.div<{ $on: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${p =>
    p.$on ? `linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400})` : tokens.purple100};
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
`

const ToggleThumb = styled.div<{ $on: boolean }>`
  position: absolute;
  top: 2px;
  left: ${p => (p.$on ? '18px' : '2px')};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: white;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`

const AdminNote = styled.p`
  font-size: 11px;
  color: ${tokens.purple400};
  margin-top: 4px;
`

const IntegrationsSection = styled.div`
  margin-top: 4px;
  border-top: 1px solid ${tokens.purple100};
  padding-top: 16px;
`

const IntegrationsSectionLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  color: ${tokens.purple400};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
`

const IntegrationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`

const IntegrationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IntegrationDot = styled.div<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => (p.$active ? '#3b6d11' : tokens.purple100)};
  flex-shrink: 0;
`

const IntegrationName = styled.span`
  font-size: 13px;
  color: ${tokens.purple900};
`

const IntegrationStatus = styled.span<{ $active: boolean }>`
  font-size: 11px;
  color: ${p => (p.$active ? '#3b6d11' : tokens.purple400)};
`

const PROVIDERS: { key: Provider; label: string }[] = [
  { key: 'google', label: 'Google' },
  { key: 'apple', label: 'Apple' },
]

export function UserForm({ initial, onChange, onClearIntegration }: Props) {
  const [email, setEmail] = useState(initial?.email ?? '')
  const [name, setName] = useState(initial?.name ?? '')
  const [password, setPassword] = useState('')
  const [isAdmin, setIsAdmin] = useState(initial?.isAdmin ?? false)
  const [locale, setLocale] = useState(initial?.locale ?? 'en')

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    const payload: CreateUserPayload | UpdateUserPayload = initial
      ? { name, email, isAdmin, locale } // update — no password
      : { name, email, password, isAdmin, locale } // create — password required
    onChangeRef.current(payload)
  }, [name, email, password, isAdmin, locale, initial])

  return (
    <>
      <Row>
        <FormField label="Full name">
          <Input
            type="text"
            placeholder="Jane Smith"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </FormField>
        <FormField label="Locale">
          <Select value={locale} onChange={e => setLocale(e.target.value)}>
            <option value="en">English</option>
            <option value="es">Español</option>
          </Select>
        </FormField>
      </Row>

      <FormField label="Email address">
        <Input
          type="email"
          placeholder="jane@carameru.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
      </FormField>

      {!initial && (
        <FormField
          label="Password"
          hint="Min. 8 characters — user can change this after logging in"
        >
          <Input
            type="password"
            placeholder="Temporary password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </FormField>
      )}

      <FormField label="Role">
        <Toggle onClick={() => setIsAdmin(v => !v)}>
          <ToggleTrack $on={isAdmin}>
            <ToggleThumb $on={isAdmin} />
          </ToggleTrack>
          {isAdmin ? 'Admin' : 'Regular user'}
        </Toggle>
        <AdminNote>
          {isAdmin
            ? 'Admin users have full access to the admin panel'
            : 'Regular users only have access to the client app'}
        </AdminNote>
      </FormField>

      {initial && onClearIntegration && (
        <IntegrationsSection>
          <IntegrationsSectionLabel>Connected accounts</IntegrationsSectionLabel>
          {PROVIDERS.map(({ key, label }) => {
            const active = initial.integrations[key]
            return (
              <IntegrationRow key={key}>
                <IntegrationInfo>
                  <IntegrationDot $active={active} />
                  <IntegrationName>{label}</IntegrationName>
                  <IntegrationStatus $active={active}>
                    {active ? 'Connected' : 'Not connected'}
                  </IntegrationStatus>
                </IntegrationInfo>
                {active && (
                  <Button variant="ghost" size="sm" onClick={() => onClearIntegration(key)}>
                    Reset
                  </Button>
                )}
              </IntegrationRow>
            )
          })}
        </IntegrationsSection>
      )}
    </>
  )
}
